import { z } from 'zod'
import { authPasswordSchema, loginPhoneSchema } from './input-validation'

/** Login form contract — shared by the RHF resolver and the request body. */
export const loginInputSchema = z.object({
  /** Dial code including the plus, e.g. `+62`. */
  dialCode: z.string().min(2, 'Pilih kode negara.'),
  phone: loginPhoneSchema,
  password: authPasswordSchema,
})

/**
 * POST /v2/account/auth/mail/login — Google ID-token exchange (WEBLP-2291).
 * `token` is the GIS `credential` JWT, not an OAuth2 access token.
 * `device` must be `robojastip-web` so account validates against
 * `web_robojastip_client_id`, not the Lion Parcel website client ID.
 */
export const googleLoginRequestSchema = z.object({
  device: z.literal('robojastip-web'),
  token: z.string().min(1),
})

/**
 * POST /v3/account/auth/login — Android `AccessTokenResponse` /
 * account `token.ResultResponse`.
 */
export const loginTokenSchema = z
  .object({
    token: z.string().min(1),
    type: z.string().optional(),
    expired_at: z.string().min(1),
    account_id: z.union([z.string(), z.number()]).optional(),
    refresh_token: z.string().nullish(),
    require_change_password: z.boolean().optional(),
    country_code: z.string().optional(),
  })
  .passthrough()

const optionalId = z.union([z.string(), z.number()]).nullish()
const optionalText = z.union([z.string(), z.number()]).nullish()

/**
 * GET /v2/account/profile/me — Android `AccountResponse`.
 * IDs arrive as number or string; name keys vary across account builds.
 */
export const accountProfileSchema = z
  .object({
    customer_id: optionalId,
    account_id: optionalId,
    id: optionalId,
    fullname: optionalText,
    full_name: optionalText,
    name: optionalText,
    first_name: optionalText,
    last_name: optionalText,
    shop_name: optionalText,
    store_name: optionalText,
    username: optionalText,
    phone_number: optionalText,
    email: optionalText,
    picture: optionalText,
    photo: optionalText,
    avatar: optionalText,
  })
  .passthrough()

export const userSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  avatarUrl: z.string().nullish(),
})

export const sessionSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().nullish(),
  /** ISO-8601 expiry of the access token. */
  expiresAt: z.string(),
  user: userSchema,
})

export type LoginInput = z.infer<typeof loginInputSchema>
export type LoginToken = z.infer<typeof loginTokenSchema>
export type AccountProfile = z.infer<typeof accountProfileSchema>
export type User = z.infer<typeof userSchema>
export type Session = z.infer<typeof sessionSchema>

export function toIsoExpiry(raw: string) {
  const parsed = Date.parse(raw)
  if (Number.isNaN(parsed)) {
    throw new Error('Format tanggal kedaluwarsa token tidak valid.')
  }
  return new Date(parsed).toISOString()
}

function asText(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }
  return undefined
}

function pickProfileName(profile: Partial<AccountProfile>): string {
  const joined = [asText(profile.first_name), asText(profile.last_name)]
    .filter((part): part is string => Boolean(part))
    .join(' ')

  const candidates = [
    profile.fullname,
    profile.full_name,
    profile.name,
    profile.shop_name,
    profile.store_name,
    joined,
    profile.username,
  ]
  for (const candidate of candidates) {
    const name = asText(candidate)
    if (name) return name
  }

  return 'Jastiper'
}

export function toUserFromProfile(profile: Partial<AccountProfile>, fallbackPhone: string): User {
  const id = profile.customer_id ?? profile.account_id ?? profile.id
  const idText = asText(id)

  return {
    id: idText ?? fallbackPhone,
    name: pickProfileName(profile),
    email: asText(profile.email) ?? null,
    phone: asText(profile.phone_number) ?? fallbackPhone,
    avatarUrl: asText(profile.picture) ?? asText(profile.photo) ?? asText(profile.avatar) ?? null,
  }
}

export function toSession(token: LoginToken, user: User): Session {
  return {
    accessToken: token.token,
    refreshToken: token.refresh_token,
    expiresAt: toIsoExpiry(token.expired_at),
    user,
  }
}
